import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import './App.css';
import Header from "./Components/Header"
import { createStore, combineReducers } from 'redux';
import TrackerReducer from'./Redux/TrackerReducer';
import OverlayReducer from'./Redux/OverlayReducer';
import {Provider} from 'react-redux'
import OverlayHandler from './Components/Overlays/OverlayHandler';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './Components/ErrorBoundary';

// Critical routes - loaded immediately (main tracker view)
import TrackerView from "./Components/Tracker/TrackerView"

// Lazy-loaded routes - code split for faster initial load
const CallView = lazy(() => import("./Components/Call/CallView"));
const SecurexTrackerView = lazy(() => import("./Components/Tracker/SecurexTrackerView"));
const InfoView = lazy(() => import("./Components/Info/InfoView"));
const MenuView = lazy(() => import("./Components/Menu/MenuView"));
const AddressView = lazy(() => import('./Components/Address/AddressView'));
const AmbiguousAddressView = lazy(() => import('./Components/Address/AmbiguousAddressView'));
const SilentAddressView = lazy(() => import('./Components/SilentAddress/SilentAddressView'));
const AmbiguousSilentAddressView = lazy(() => import('./Components/SilentAddress/AmbiguousSilentAddressView'));
const ReserverenView = lazy(() => import('./Components/Reserveren/ReserverenView'));
const NotFound = lazy(() => import('./Components/Error/NotFound'));
const CadeauView = lazy(() => import('./Components/Cadeau/CadeauView'));
const SetCookieView = lazy(() => import('./Components/SetCookie/SetCookieView'));
const SetTokenView = lazy(() => import('./Components/SetToken/SetTokenView'));
const PrivacyPolicyView = lazy(() => import('./Components/PrivacyPolicy/PrivacyPolicyView'));
const CookiePolicyView = lazy(() => import('./Components/CookiePolicy/CookiePolicyView'));
const Onboarding = lazy(() => import('./Components/Onboarding/Onboarding'));
const ConfigEditor = lazy(() => import('./Components/Admin/ConfigEditor'));

export const store = createStore(combineReducers({tracker: TrackerReducer, overlay: OverlayReducer}), window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__());

// Loading fallback for lazy-loaded components
function LoadingFallback() {
  return (
    <div className="loading-fallback" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div className="loading-spinner" style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e0e0e0',
        borderTopColor: '#ffd700',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function AppContent() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { meta, getTranslation, loading } = useConfig();

  // Get onboarding config from current language translation
  const currentLang = localStorage.getItem('language') || meta?.defaultLanguage || 'nl';
  const translation = getTranslation(currentLang);
  const onboardingEnabled = translation?.onboarding?.enabled !== false;

  useEffect(() => {
    // Check if onboarding should be shown
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');
    if (onboardingCompleted !== 'true' && onboardingEnabled && !loading) {
      setShowOnboarding(true);
    }
  }, [onboardingEnabled, loading]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  return (
    <div>
      {showOnboarding && (
        <Suspense fallback={<LoadingFallback />}>
          <Onboarding onComplete={handleOnboardingComplete} />
        </Suspense>
      )}
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Header></Header>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/securex" element={<SecurexTrackerView />} />
            <Route path="/securextest" element={<SecurexTrackerView />} />

            <Route path="/" element={<TrackerView />} />
            <Route path="/address" element={<AddressView />} />
            <Route path="/address/ambiguous" element={<AmbiguousAddressView />} />
            <Route path="/silentaddress" element={<SilentAddressView />} />
            <Route path="/silentaddress/ambiguous" element={<AmbiguousSilentAddressView />} />
            <Route path="/call" element={<CallView />} />
            <Route path="/info" element={<InfoView />} />
            <Route path="/about" element={<InfoView />} />
            <Route path="/aboutapp" element={<InfoView />} />
            <Route path="/status" element={<InfoView />} />
            <Route path="/menu" element={<MenuView />} />
            <Route path="/reserveren" element={<ReserverenView />} />
            <Route path="/cadeau/:barcode?" element={<CadeauView />} />
            <Route path="/setcookie/:apikey?/:division_id?" element={<SetCookieView />} />
            <Route path="/settoken" element={<SetTokenView />} />
            <Route path="/privacypolicy" element={<PrivacyPolicyView />} />
            <Route path="/cookiepolicy" element={<CookiePolicyView />} />
            <Route path="/admin/config" element={<ConfigEditor />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <OverlayHandler></OverlayHandler>
      </Router>
    </div>
  );
}

function AppWithLanguage() {
  const { meta } = useConfig();

  return (
    <LanguageProvider
      supportedLanguages={meta?.supportedLanguages}
      defaultLanguage={meta?.defaultLanguage}
    >
      <AppContent />
    </LanguageProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ConfigProvider>
          <AppWithLanguage />
        </ConfigProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
